import React, { useEffect, useRef } from "react";
import dashjs from "dashjs";
import { useSelector } from "react-redux";

const DashPlayer = ({ url, token, language, videoId }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const userState = useSelector((state) => state.user);

  const basePath = `${language}/${videoId}`;

  useEffect(() => {
    if (!url || !token) {
      console.warn("Missing required props:", {
        hasUrl: !!url,
        hasToken: !!token,
      });
      return;
    }

    try {
      const player = dashjs.MediaPlayer().create();
      playerRef.current = player;

      // Custom request modifier
      player.extend(
        "RequestModifier",
        () => ({
          modifyRequestHeader: (xhr) => {
            if (xhr instanceof XMLHttpRequest) {
              // Debug log
              console.log(
                "Modifying request headers for:",
                xhr.url || "unknown URL"
              );

              xhr.removeRequestHeader?.("Authorization");
              xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            }
            return xhr;
          },
          modifyRequestURL: (url) => {
            console.log("Original URL:", url);

            if (url.includes("init-stream") || url.includes("chunk-stream")) {
              // Get the stream part (init-stream0.m4s or chunk-stream-xxx.m4s)
              const streamPart = url.split("/").pop();

              // Construct the path that needs to be encoded
              const pathToEncode = `${language}/${videoId}/${streamPart}`;

              // Encode the entire path
              const encodedPath = encodeURIComponent(pathToEncode);

              // Construct the final URL
              const newUrl = `${process.env.REACT_APP_API_URL}/Recipe/GetVideoPart/${encodedPath}`;

              console.log("Modified URL:", newUrl);
              return newUrl;
            }
            return url;
          },
        }),
        true
      );

      // Enable credentials for all request types
      const types = [
        "MPD",
        "MediaSegment",
        "InitializationSegment",
        "BitstreamSwitchingSegment",
        "FragmentInfoSegment",
        "IndexSegment",
      ];

      types.forEach((type) => {
        player.setXHRWithCredentialsForType(type, true);
      });

      // Set up player configuration
      player.updateSettings({
        streaming: {
          xhr: {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          retryAttempts: {
            MPD: 3,
            MediaSegment: 3,
            InitializationSegment: 3,
            other: 3,
          },
        },
      });

      // Initialize player
      player.initialize(videoRef.current, url, true);

      // Enhanced error logging
      player.on("error", (e) => {
        console.error("Player error:", e);
        console.log("Current token status:", {
          tokenLength: token?.length,
          urlUsed: url,
        });
      });

      player.on("manifestLoaded", () => {
        console.log("Manifest loaded successfully");
      });

      player.on("streamInitialized", () => {
        console.log("Stream initialized successfully");
      });

      player.on("requestError", (e) => {
        console.error("Request error:", {
          status: e.status,
          url: e.url,
          type: e.type,
        });
      });

      return () => {
        try {
          if (playerRef.current) {
            playerRef.current.destroy();
            playerRef.current = null;
          }
        } catch (error) {
          console.error("Error during cleanup:", error);
        }
      };
    } catch (error) {
      console.error("Error initializing dash.js player:", error);
    }
  }, [url, token]);

  // Add component mount debug
  useEffect(() => {
    console.log(
      "DashPlayer mounted with token:",
      token ? "present" : "missing"
    );
  }, []);

  return (
    <video
      ref={videoRef}
      controls
      style={{ width: "100%" }}
      onError={(e) => console.error("Video element error:", e.target.error)}
    >
      <p>Your browser does not support HTML5 video.</p>
    </video>
  );
};

// Add prop validation
DashPlayer.defaultProps = {
  url: "",
  token: "",
};

export default DashPlayer;
